import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSecondsSince } from './liveCallsContext';
import { type HostPhoneState, NO_HOST_PHONE, type PhoneHost } from '../../embed/phoneState';
import type { Inbox } from '../../api/types';
import type { TwilioPhone } from './useTwilioPhone';

/**
 * The Twilio softphone when the DASHBOARD holds it, wearing the same shape as
 * the one this module registers for itself.
 *
 * Inside the dispatch dashboard the device cannot live here. This module is
 * framed by the communication route and disposed the moment the agent opens a
 * reservation, which took the registered device — and whatever call was on it —
 * with it. The dashboard mounts the console's `/phone` frame in its shell
 * instead, where it outlives every page change, and the module drives that.
 *
 * What is mirrored is what OFFERING a call needs — the line it would leave on,
 * and the reason it could not — and what NAMING one needs: the number, when
 * it connected, and the conversation the dashboard matched it to, so the call
 * bar can say who the agent is talking to over the thread and open it. What
 * is not mirrored is the call itself — answering, muting and hanging up are
 * the phone panel's own buttons, and it is on screen on every dashboard page
 * including this one. So the control methods stand empty here, and CallBar
 * draws a strip with no controls on it rather than a second, duller copy of
 * that panel.
 */
export function useHostPhone(host: PhoneHost, inboxes: Inbox[]): TwilioPhone {
  const [state, setState] = useState<HostPhoneState>(NO_HOST_PHONE);

  useEffect(() => host.subscribe(setState), [host]);

  // Null until the inbox list has caught up with the phone: the id is the
  // dashboard's answer, and naming a channel this agent cannot see would put a
  // caller id on the Call button that the server would then refuse.
  const inbox = useMemo(
    () =>
      state.available
        ? (inboxes.find((candidate) => candidate.id === state.inboxId) ?? null)
        : null,
    [inboxes, state.available, state.inboxId],
  );

  const call = useCallback(
    async (to: string) => {
      host.send({ action: 'dial', to });
    },
    [host],
  );
  const retry = useCallback(() => host.send({ action: 'retry' }), [host]);
  // The dashboard's panel, not one of ours: `open` is the one thing the
  // module may ask of it — the New call control on the Calls tab presses this.
  const showPanel = useCallback(() => host.send({ action: 'open' }), [host]);
  const noop = useCallback(() => {}, []);
  const never = useCallback(async () => false, []);
  const nothing = useCallback(async () => {}, []);
  const seconds = useSecondsSince(state.since);

  return {
    inbox,
    // 'off' is what "no phone over there" has to look like: every reason the
    // module gives for a blocked call reads off this, and reporting a status
    // for a device that is not there would name a fault that is not one.
    status: state.available ? state.status : 'off',
    error: state.error,
    micBlocked: state.micBlocked,
    peer: state.available ? state.peer : null,
    outbound: state.outbound,
    muted: false,
    seconds,
    since: state.available ? state.since : null,
    call,
    accept: noop,
    reject: noop,
    hangUp: noop,
    toggleMute: noop,
    retry,
    // The controls are the frame's: the dashboard's panel draws them beside
    // the call it holds. This side has no leg to act on.
    callSid: null,
    canControl: false,
    recording: 'on',
    conferenced: false,
    controlBusy: false,
    controlError: null,
    sendDigits: noop,
    pauseRecording: nothing,
    resumeRecording: nothing,
    transfer: never,
    addParticipant: never,
    participants: [],
    removeParticipant: never,
    conversationDisplayId: state.available ? state.conversationDisplayId : null,
    hosted: true,
    panelOpen: false,
    showPanel,
    hidePanel: noop,
  };
}
